/**
 * useLiveMountTelemetry — Live Restart Investigation instrumentation.
 *
 * Attaches a single hook to LiveRoom that emits one event per interesting
 * lifecycle transition, all tagged with a stable `session_correlation_id`
 * so operators can grep the timeline of a single Live visit end-to-end.
 *
 * Signals captured (mapped to the 9 buckets from the investigation plan):
 *   1. full page reload            → mount:reload   (from performance nav.type)
 *   2. React LiveRoom remount      → mount:remount  (same sid, new mount id)
 *   3. LiveKit room/token reconnect → token:refresh, livekit:reconnecting
 *   4. WebSocket reconnect         → ws:reconnect   (via wsConnected transition)
 *   5. route/navigation change     → nav:route-change (pathname change while mounted)
 *   6. auth/session refresh        → auth:refresh   (via AuthContext user identity swap)
 *   7. background/foreground       → lifecycle:hidden / lifecycle:visible
 *   8. service-worker / PWA update → sw:update
 *   9. memory / OOM teardown       → memory:pressure (heap > 85% of limit)
 *
 * PRIVACY — no message contents, no payment/PII, no free-text UA. `stream_id`
 * is public. `user_id` is server-attached from the auth cookie; the payload
 * itself never carries it.
 *
 * Preview-only side effect: silently no-ops if the backend has
 * LIVE_MOUNT_TELEMETRY_ENABLED unset — the endpoint returns 204 without
 * writing. So the hook is safe to ship to Prod dark.
 */
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

const SID_KEY_PREFIX = "pandax_live_sid_";
const MOUNT_INDEX_KEY = "pandax_live_mount_index";

function _uuid() {
  // RFC-4122 v4-ish; window.crypto if available, otherwise Math.random fallback.
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "sid-" + Math.random().toString(16).slice(2) + Date.now().toString(16);
}

function _coarsePlatform() {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  if (/macintosh|windows|linux/.test(ua)) return "desktop";
  return "other";
}

function _navType() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    return nav?.type || "unknown";
  } catch {
    return "unknown";
  }
}

function _memorySnapshot() {
  const out = {};
  try {
    if (performance?.memory) {
      out.js_heap_used_mb = Math.round(performance.memory.usedJSHeapSize / 1e6);
      out.js_heap_limit_mb = Math.round(performance.memory.jsHeapSizeLimit / 1e6);
    }
    if (navigator?.deviceMemory) out.device_memory_gb = navigator.deviceMemory;
  } catch { /* noop */ }
  return out;
}

function _isPWA() {
  try {
    return window.matchMedia?.("(display-mode: standalone)")?.matches
        || window.navigator?.standalone === true;
  } catch { return false; }
}

function _post(payload) {
  // Fire-and-forget; the endpoint returns 204 and we never inspect it. Any
  // failure (network, backend disabled, blocked by adblocker) is swallowed
  // so we do NOT introduce ANOTHER remount cause via a runtime error.
  try {
    api.post("/telemetry/live-mount", payload).catch(() => {});
  } catch { /* noop */ }
}

export default function useLiveMountTelemetry({
  streamId,
  role,
  token,
  wsConnected,
  authUserId,
}) {
  const sidKey = `${SID_KEY_PREFIX}${streamId || "unknown"}`;
  const sidRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const tokenRefreshCountRef = useRef(0);
  const wsReconnectCountRef = useRef(0);
  const prevWsRef = useRef(false);
  const prevTokenRef = useRef(null);
  const prevUserRef = useRef(authUserId);
  const prevPathRef = useRef(typeof window !== "undefined" ? window.location.pathname : "");
  const emittedInitialRef = useRef(false);

  // --- initial mount event -------------------------------------------------
  useEffect(() => {
    if (!streamId) return;
    if (emittedInitialRef.current) return;
    emittedInitialRef.current = true;
    const mountedAt = mountedAtRef.current;

    let sid = null;
    try { sid = sessionStorage.getItem(sidKey); } catch { /* noop */ }
    // Increment a per-tab mount counter so remounts are visible in the log.
    let mountIndex = 0;
    try {
      mountIndex = parseInt(sessionStorage.getItem(MOUNT_INDEX_KEY) || "0", 10) + 1;
      sessionStorage.setItem(MOUNT_INDEX_KEY, String(mountIndex));
    } catch { /* noop */ }

    const navType = _navType();
    let event = "mount:initial";
    let reason = "initial";
    if (sid) {
      // Session id was already in storage — either a reload or a remount.
      if (navType === "reload") { event = "mount:reload"; reason = "reload"; }
      else                       { event = "mount:remount"; reason = "remount"; }
    } else {
      sid = _uuid();
    }
    try { sessionStorage.setItem(sidKey, sid); } catch { /* noop */ }
    sidRef.current = sid;

    _post({
      session_correlation_id: sid,
      stream_id: streamId,
      role,
      event,
      reason,
      meta: {
        platform: _coarsePlatform(),
        nav_type: navType,
        was_pwa: _isPWA(),
        mount_index: mountIndex,
        ..._memorySnapshot(),
      },
    });

    // page-hide / freeze / resume — bfcache-safe lifecycle signals.
    const onVis = () => {
      const sidNow = sidRef.current;
      if (!sidNow) return;
      _post({
        session_correlation_id: sidNow,
        stream_id: streamId,
        role,
        event: document.visibilityState === "hidden" ? "lifecycle:hidden" : "lifecycle:visible",
        reason: "visibility-change",
        meta: {
          platform: _coarsePlatform(),
          was_hidden: document.visibilityState === "hidden",
          elapsed_ms_since_mount: Date.now() - mountedAtRef.current,
          ..._memorySnapshot(),
        },
      });
    };
    document.addEventListener("visibilitychange", onVis);

    const onUnload = () => {
      const sidNow = sidRef.current;
      if (!sidNow) return;
      // sendBeacon so the request survives navigation. Endpoint accepts JSON.
      try {
        const body = JSON.stringify({
          session_correlation_id: sidNow,
          stream_id: streamId,
          role,
          event: "unmount:unload",
          reason: "unknown",
          meta: {
            platform: _coarsePlatform(),
            elapsed_ms_since_mount: Date.now() - mountedAtRef.current,
          },
        });
        // Beacon URL must match the api base — use api's config if possible.
        const base = api?.defaults?.baseURL || "";
        navigator.sendBeacon?.(`${base}/telemetry/live-mount`, new Blob([body], { type: "application/json" }));
      } catch { /* noop */ }
    };
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);

    // service-worker skipWaiting is the classic PWA-update remount cause.
    let swController = null;
    const onSwChange = () => {
      const sidNow = sidRef.current;
      if (!sidNow) return;
      _post({
        session_correlation_id: sidNow,
        stream_id: streamId,
        role,
        event: "sw:update",
        reason: "sw-update",
        meta: { platform: _coarsePlatform() },
      });
    };
    try {
      swController = navigator.serviceWorker?.controller || null;
      navigator.serviceWorker?.addEventListener?.("controllerchange", onSwChange);
    } catch { /* noop */ }

    // memory:pressure — poll every 20s, emit once when heap > 85% of limit.
    let memInterval = null;
    let firedMem = false;
    try {
      if (performance?.memory) {
        memInterval = setInterval(() => {
          if (firedMem) return;
          const used = performance.memory.usedJSHeapSize;
          const limit = performance.memory.jsHeapSizeLimit;
          if (limit > 0 && used / limit > 0.85) {
            firedMem = true;
            _post({
              session_correlation_id: sidRef.current,
              stream_id: streamId,
              role,
              event: "memory:pressure",
              reason: "unknown",
              meta: _memorySnapshot(),
            });
          }
        }, 20000);
      }
    } catch { /* noop */ }

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
      try { navigator.serviceWorker?.removeEventListener?.("controllerchange", onSwChange); } catch { /* noop */ }
      if (memInterval) clearInterval(memInterval);
      // React unmount — mark it so we know the React tree removed us
      // (distinct from a full page unload). Suppress the emission when it
      // fires < 500ms after mount because that's React 18/19 StrictMode's
      // dev-only double-invocation of effects (production stripped) —
      // recording it would just add noise to the timeline.
      const elapsed = Date.now() - mountedAt;
      if (elapsed < 500) return;
      _post({
        session_correlation_id: sidRef.current,
        stream_id: streamId,
        role,
        event: "unmount:react",
        reason: "unknown",
        meta: {
          platform: _coarsePlatform(),
          elapsed_ms_since_mount: elapsed,
        },
      });
    };
  // Intentional: initial mount effect. Downstream state (token, wsConnected,
  // authUserId, pathname) is watched by dedicated effects below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  // --- token refresh signal ------------------------------------------------
  useEffect(() => {
    if (!sidRef.current || !streamId) return;
    if (prevTokenRef.current === null) {
      prevTokenRef.current = token;
      return;
    }
    if (prevTokenRef.current !== token) {
      tokenRefreshCountRef.current += 1;
      _post({
        session_correlation_id: sidRef.current,
        stream_id: streamId,
        role,
        event: "token:refresh",
        // We don't know the trigger here — the two known call sites in
        // LiveRoom (cohost accept, cohost modal accept) can be enriched
        // later by having them call a tagged refresh() variant.
        reason: "unknown",
        meta: {
          platform: _coarsePlatform(),
          token_refresh_index: tokenRefreshCountRef.current,
          elapsed_ms_since_mount: Date.now() - mountedAtRef.current,
        },
      });
      prevTokenRef.current = token;
    }
  }, [token, streamId, role]);

  // --- WebSocket reconnect signal -----------------------------------------
  useEffect(() => {
    if (!sidRef.current || !streamId) return;
    if (prevWsRef.current && !wsConnected) {
      _post({
        session_correlation_id: sidRef.current,
        stream_id: streamId,
        role,
        event: "ws:close",
        reason: "ws-reconnect",
        meta: {
          platform: _coarsePlatform(),
          elapsed_ms_since_mount: Date.now() - mountedAtRef.current,
        },
      });
    }
    if (!prevWsRef.current && wsConnected && mountedAtRef.current) {
      const wasReconnect = wsReconnectCountRef.current > 0;
      wsReconnectCountRef.current += 1;
      _post({
        session_correlation_id: sidRef.current,
        stream_id: streamId,
        role,
        event: wasReconnect ? "ws:reconnect" : "ws:open",
        reason: wasReconnect ? "ws-reconnect" : "initial",
        meta: {
          platform: _coarsePlatform(),
          ws_reconnect_index: wsReconnectCountRef.current,
          elapsed_ms_since_mount: Date.now() - mountedAtRef.current,
        },
      });
    }
    prevWsRef.current = wsConnected;
  }, [wsConnected, streamId, role]);

  // --- auth identity swap --------------------------------------------------
  useEffect(() => {
    if (!sidRef.current || !streamId) return;
    if (prevUserRef.current !== authUserId && prevUserRef.current !== undefined) {
      _post({
        session_correlation_id: sidRef.current,
        stream_id: streamId,
        role,
        event: "auth:refresh",
        reason: "auth-refresh",
        meta: {
          platform: _coarsePlatform(),
          elapsed_ms_since_mount: Date.now() - mountedAtRef.current,
        },
      });
    }
    prevUserRef.current = authUserId;
  }, [authUserId, streamId, role]);

  // --- pathname watcher (route change while mounted) -----------------------
  useEffect(() => {
    if (!sidRef.current || !streamId) return;
    const check = () => {
      const now = window.location.pathname;
      if (now !== prevPathRef.current) {
        _post({
          session_correlation_id: sidRef.current,
          stream_id: streamId,
          role,
          event: "nav:route-change",
          reason: "route-change",
          meta: {
            platform: _coarsePlatform(),
            elapsed_ms_since_mount: Date.now() - mountedAtRef.current,
          },
        });
        prevPathRef.current = now;
      }
    };
    // popstate covers back/forward; a light interval catches pushState
    // (React Router uses history.push which does NOT fire popstate).
    window.addEventListener("popstate", check);
    const iv = setInterval(check, 500);
    return () => {
      window.removeEventListener("popstate", check);
      clearInterval(iv);
    };
  }, [streamId, role]);

  return sidRef;
}
